import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { fetchCustomerQuotes, quoteSelector } from '../../redux/quoteSlice/quoteSlice'
import { rentalVehicleSelector } from '../../redux/rentalSlice/rentalVehicleSlice'
import { authSelector } from '../../redux/authSlice/authSlice'
import Header1 from '../../components/header/Header1'
import config from '../../config'

const MyQuotesPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { rentalDetails } = useAppSelector(rentalVehicleSelector)
  const { quotes, loading, error } = useAppSelector(quoteSelector)
  const { userId } = useAppSelector(authSelector)

  const statusMap: { [key: number]: string } = {
    0: '⏳ Pending',
    1: '✅ Approved',
    2: '❌ Rejected'
  }

  useEffect(() => {
    if (userId) dispatch(fetchCustomerQuotes(Number(userId)))
  }, [dispatch, userId])

  return (
    <div className="min-h-screen bg-gray-50">
      <Header1 title="My Quotes" to="/rentVehicle" />

      <div className="p-6 max-w-6xl mx-auto">
        {loading && <p className="text-lg text-gray-600">Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {quotes.length === 0 ? (
          <p className="text-gray-500 text-lg text-center mt-20">
            No quotes found.
          </p>
        ) : (
          <div className="grid gap-8">
            {quotes.map((q: any) => (
              <div
                key={q.id}
                className="border rounded-2xl p-6 bg-white shadow-lg hover:shadow-xl transition-shadow"
              >
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-bold text-xl text-gray-800">
                    Quote #{q.id}
                  </h2>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      q.status === 1
                        ? 'bg-green-100 text-green-700'
                        : q.status === 2
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {statusMap[q.status] || 'Unknown'}
                  </span>
                </div>

                {/* Dates */}
                <div className="text-sm text-gray-600 mb-4">
                  <p>
                    <span className="font-semibold">From:</span>{' '}
                    {new Date(q.startDate).toLocaleDateString()}
                  </p>
                  <p>
                    <span className="font-semibold">To:</span>{' '}
                    {new Date(q.endDate).toLocaleDateString()}
                  </p>
                </div>

                {/* Address */}
                <p className="font-medium text-gray-700 mb-4">
                  📍 Delivery to:{' '}
                  <span className="text-gray-900">{q.address}</span>
                </p>

                {/* Vehicles */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  {q.vehicles.map((v: any, index: number) => (
                    <div
                      key={index}
                      className="flex flex-col items-center border rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition"
                    >
                      <img
                        src={`${config.imageBaseURL}/${v.details?.image_url}`}
                        alt={v.details?.model_name}
                        className="w-full h-24 object-contain bg-white rounded mb-2"
                      />
                      <p className="text-sm font-semibold text-gray-800 text-center truncate w-full">
                        {v.details?.model_name} (x{v.count})
                      </p>
                      <p className="text-xs text-gray-500">
                        ₹{v.details?.rental_cost_per_day}/day
                      </p>
                      <p className="text-xs text-gray-600 font-medium">
                        Subtotal: ₹
                        {(q.rentalCost + q.transportCost).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Cost breakdown */}
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span>Rental Cost</span>
                    <span className="font-medium">
                      ₹{q.rentalCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Transport Cost</span>
                    <span className="font-medium">
                      ₹{q.transportCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Operator Cost (Discounted)</span>
                    <span className="line-through">
                      ₹{q.driverCost.toLocaleString()}
                    </span>
                  </div>
                  <hr />
                  <div className="flex justify-between font-bold text-green-700 text-xl">
                    <span>Total</span>
                    <span>₹{q.totalCost.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyQuotesPage
